<script setup lang="ts">
import { computed, inject, onBeforeMount, reactive, ref, Ref, watch } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { ONEWAY, OPENJAW, SUFFIX_GROUPB, SUFFIX_PUBLIC, SUFFIX_VACV, UNASSIGNED } from '@/constants';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useWorksheetComposable } from './useWorksheetComposable';
import cloneDeep from 'lodash/cloneDeep';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import MultiSelect from 'primevue/multiselect';
import HTMLEditor from '@/components/common/HTMLEditor.vue';
import ListFlightModal from '@/components/modal/ListFlightModal.vue';
import LinkedQuotesEditor from '@/components/modal/worksheet/LinkedQuotesEditor.vue';
import PriceBreakdownModal from '@/components/modal/PriceBreakdownModal.vue';
import ViewPDFFile from '@/components/modal/utils/ViewPDFFile.vue';

const dialogRef = inject('dialogRef') as Ref;
const reloadList = dialogRef.value.options.emits.reloadList;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const listAirline = ref([]);
const disablePreviewPDF = ref(true);
const currentLanguage = getCurrentLocale();
const adminQuoteStore = useAdminQuoteStore();
const quote = computed(() => adminQuoteStore.currentQuote);

const {
  authorizations,
  canViewPromotion,
  canEditPromotion,
  canViewAgencyComment,
  canEditAgencyComment,
  hasUserPermission,
  validateFlightNumber,
  airportList,
  initFlightData,
  formatFlightData,
  onFilterAirport,
  LIST_CABIN_TYPE,
  getCabinType,
  canViewTermConditions,
  canEditTermConditions,
  isCheckValidateDate,
  handleConvertBrTag,
  formatPriceRow,
  changePricingFlight
} = useWorksheetComposable();

const canEditHeader = authorizations?.worksheet_air_only?.quote_details_header_edit;

const departureDateFormat = reactive({
  depDate: ''
});

const promoeditedData = ref({
  index: null,
  data: ''
});

const editComment = ref({
  index: null,
  data: ''
});

const editIntro = ref({
  isEdit: false,
  data: ''
});

const editTermConditions = ref({
  isEdit: false,
  data: ''
});

const expandedDetail = ref<any>([[]]);
const editingRows = ref([]);
const editingPriceRows = ref([]);
const filterGateway = ref([]);
const filterDestination = ref([]);
let typeProcess = [['Economy']];
let detailClasses: any[] = [];

const isProcessButton = (air, type: string, index: string) => {
  const airType = air?.price.map((pr) => pr.type);
  const indexOf = airType.indexOf(type);
  return index === indexOf;
};

const expandPricingFunc = (type: string, airIndex: number, index: number) => {
  expandedDetail.value[airIndex][index][type] = !expandedDetail.value[airIndex][index][type];
  typeProcess[airIndex][index] = type;
};

const formatDateTime = (date) => {
  return DateTime.fromJSDate(new Date(date)).toFormat('yyyy-LL-dd HH:mm').replace(' ', 'T');
};

const getClassAir = (price) => {
  return t(`worksheet.${(price?.find((priceItem) => priceItem.type === 'Economy')?.type ?? 'Economy').toLowerCase()}`);
};

const getClassAirBusiness = (price) => {
  return t(`worksheet.${(price?.find((priceItem) => priceItem.type === 'Business')?.type ?? 'Business').toLowerCase()}`);
};

const getTime = (date) => {
  return date ? DateTime.fromJSDate(new Date(date)).toFormat('T') : '';
};

const onRowEditSave = ({ newData, index }, value) => {
  let depTime = newData?.depTime?.toString();
  let arrTime = newData?.arrTime?.toString();

  if (typeof newData.gate === 'object') {
    newData.gateCode = newData.gate.airportCode;
    newData.gate = `${newData.gate.airportName} (${newData.gate.airportCode})`;
  }

  if (typeof newData.dest === 'object') {
    newData.destCode = newData.dest.airportCode;
    newData.dest = `${newData.dest.airportName} (${newData.dest.airportCode})`;
  }
  newData.isNew = false;

  if (depTime?.length > 5) {
    depTime = getTime(depTime);
  }
  if (arrTime?.length > 5) {
    arrTime = getTime(arrTime);
  }
  if (isCheckValidateDate(newData, value)) {
    value[index] = { ...newData, depTime, arrTime };
    value[index].airline = listAirline.value?.find((air) => air.code === value[index].operated)?.name;
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('worksheet.validate_date') });
  }
};

const formatPrice = (priceList, permission) => {
  const listPricing = [];
  priceList?.forEach((item, pIndex) => {
    const listRows = formatPriceRow(item, permission);
    listPricing.push({
      type: item.type,
      comment: item.comment,
      promotions: item.promotions,
      pIndex,
      rows: listRows
    });
  });
  return listPricing;
};

const updateAirDetails = (airIndex: number, detailClasses: any, fareBasisCodes: string[]) => {
  let details: any = {};
  if (quote.value.airs[airIndex].details) {
    details = JSON.parse(quote.value.airs[airIndex].details);
    for (const key in detailClasses[airIndex]) {
      if (Object.prototype.hasOwnProperty.call(detailClasses[airIndex], key)) {
        if (Object.prototype.hasOwnProperty.call(details, key)) {
          const detailTemp = [...details[key], ...detailClasses[airIndex][key]];
          details[key] = [...new Set(detailTemp)];
        } else {
          details[key] = detailClasses[airIndex][key];
        }
      }
    }
  } else {
    details = detailClasses[airIndex];
  }
  const result: any = {};
  for (const key in details) {
    if (Object.prototype.hasOwnProperty.call(details, key)) {
      result[key] = details[key].filter((item: string) => fareBasisCodes.includes(item));
    }
  }
  return result;
};

const updateData = async (data) => {
  const prices = data.classSelected.map((classSelect: any) => {
    const airPrice = cloneDeep(quote.value.airs[data.airIndex]?.price[data.pricingIndex]);
    airPrice.fareCharter = classSelect.data.charter;
    airPrice.fareVACV = classSelect.data.vac;
    airPrice.fareGroupsB = classSelect.data.groupsB;
    airPrice.farePub = classSelect.data.published;
    airPrice.numberOfPassengers = classSelect.data.numberOfPassengersFares;
    airPrice.fareTax = handleParseFloat(classSelect.data.taxes);
    airPrice.fare = handleParseFloat(classSelect.data.basePrice);
    airPrice.fareBasis = classSelect.data.fareBasis;
    airPrice.basePrice = classSelect.data.basePrice;
    airPrice.paxFareSumAdult = classSelect.data.paxFareSumAdult;
    airPrice.paxFareSumChild = classSelect.data.paxFareSumChild;
    airPrice.adt = handleParseFloat(classSelect.data.marginFare);
    airPrice.chd = handleParseFloat(classSelect.data.marginFare);
    airPrice.adtTax = handleParseFloat(classSelect.data.taxes);
    airPrice.chdTax = handleParseFloat(classSelect.data.taxes);
    airPrice.suffixKey = classSelect.data.suffixKey;
    airPrice.fareTypeSelected = classSelect.data.fareTypeSelected;
    airPrice.breakdownPrice = handleBreakdownPrice(classSelect);
    airPrice.adtNo = 0;
    airPrice.chdNo = 0;
    airPrice.parties = [];
    return airPrice;
  });
  const typeFilter = quote.value.airs[data.airIndex].price.filter((price) => price.type === quote.value.airs[data.airIndex].price[data.pricingIndex].type);
  quote.value.airs[data.airIndex].price.splice(data.pricingIndex, typeFilter.length, ...prices);
  const fareBasisCodes = quote.value.airs[data.airIndex].price.flatMap((price: any) => price.fareBasis?.split(' / '));
  quote.value.airs[data.airIndex].details = updateAirDetails(data.airIndex, detailClasses, fareBasisCodes);
  quote.value.airs[data.airIndex].flights.forEach((flight: any) => {
    const { flightNo } = flight;
    if (typeof quote.value.airs[data.airIndex].details === 'object' && Object.prototype.hasOwnProperty.call(quote.value.airs[data.airIndex].details, flightNo)) {
      const classes = quote.value.airs[data.airIndex].details[flightNo];
      let matchedClasses: any = [];
      fareBasisCodes.forEach((code: string) => {
        if (classes.includes(code)) {
          matchedClasses.push(code);
        }
      });
      const cabins: any[] = [];
      quote.value.airs[data.airIndex].price.forEach((price: any) => {
        if (price.fareBasis) {
          const fareBasisArray = price.fareBasis.split(' / ');
          if (matchedClasses.some((matchedClass: string) => fareBasisArray.includes(matchedClass)) && !cabins.includes(price.type)) {
            cabins.push(price.type);
          }
        }
      });
      flight.cabin = cabins;
      matchedClasses = simplifyMatchedClasses(matchedClasses);

      if (matchedClasses.length) {
        flight.fClass = matchedClasses.join(', ');
      }
    }
  });

  quote.value?.airs.forEach((air) => {
    air.flights = formatFlightData(air.flights, true);
  });
  quote.value.airs[data.airIndex].details = JSON.stringify(quote.value.airs[data.airIndex].details);
};

const simplifyMatchedClasses = (classes: string[]) => {
  const seen: string[] = [];
  const result: string[] = [];

  classes.forEach((code: string) => {
    const firstChar = code.charAt(0);
    if (!seen.includes(firstChar)) {
      seen.push(firstChar);
      result.push(firstChar);
    }
  });
  return result;
};

const handleBreakdownPrice = (classSelect: any) => {
  switch (classSelect.data.suffixKey) {
    case SUFFIX_PUBLIC:
      return [
        ...(classSelect.data.publishedTax ?? []),
        {
          airTaxCode: 'FARE_BASE',
          airTaxType: null,
          airTaxDes: null,
          adtFare: classSelect.data.basePublishedPrice
        }
      ];
    case SUFFIX_GROUPB:
      return [
        ...(classSelect.data.groupsBTax ?? []),
        {
          airTaxCode: 'FARE_BASE',
          airTaxType: null,
          airTaxDes: null,
          adtFare: classSelect.data.baseGroupsBPrice
        }
      ];
    case SUFFIX_VACV:
      return [
        ...(classSelect.data.vacTax ?? []),
        {
          airTaxCode: 'FARE_BASE',
          airTaxType: null,
          airTaxDes: null,
          adtFare: classSelect.data.baseVacPrice
        }
      ];
    default:
      return [];
  }
};

const calculateTotalPassenger = (air, type) => {
  return type == 'Economy' ? air.adtNo + air.chdNo : air.businessAdtNo + air.businessChdNo;
};

const openFlightModal = async (air, airIndex, pricing, pricingIndex, quote) => {
  for (let i = 0; i < air.price.length; i++) {
    if (!expandedDetail.value[airIndex][i][typeProcess[airIndex][i]]) {
      typeProcess[airIndex][i] = '';
    }
  }
  const dialogRef = dialog.open(ListFlightModal, {
    props: {
      header: t('worksheet.flight_details'),
      style: { width: '90vw' },
      modal: true,
      maximizable: true
    },
    data: {
      air,
      airIndex,
      passenger: calculateTotalPassenger(air, pricing.type),
      pricing,
      pricingIndex,
      quote,
      role: authorizations?.worksheet_air_only
    },
    emits: {
      detailClassesEmit: (detail: any) => {
        onDetailClassesEmit(detail);
      },
      populateWorksheet: (data) => {
        updateData(data);
        dialogRef.close();
      }
    }
  });
};
const onDetailClassesEmit = (detail: any) => {
  if (detailClasses.length && detailClasses[detail.airIndex] && detailClasses[detail.airIndex].length) {
    for (let i = 0; i < detail.detailClasses.length; i++) {
      for (const key in detail.detailClasses[i]) {
        detailClasses[i][key] = [...detailClasses[i][key], ...detail.detailClasses[i][key]];
      }
    }
  } else {
    detailClasses = detail.detailClasses;
  }
};

const handleSavePromo = (air) => {
  air.promotion = promoeditedData.value.data;
  promoeditedData.value = { index: null, data: '' };
};

const saveComment = (air) => {
  air.comment = editComment.value.data;
  editComment.value = { index: null, data: '' };
};

const handleSaveIntro = (flightData) => {
  flightData.intro = editIntro.value.data;
  editIntro.value = {
    isEdit: false,
    data: ''
  };
};

const handleSaveTermCondition = (flightData) => {
  flightData.termConditions = editTermConditions.value.data;
  editTermConditions.value = {
    isEdit: false,
    data: ''
  };
};

const handleUpdateQuote = () => {
  const { qType, airs, sk, expires, bookedDate, intro, termConditions } = initPayloadSubmit();
  const payload = {
    qType,
    airs,
    intro: handleConvertBrTag(intro),
    expires: new Date(formatDate(expires, undefined, false)),
    bookedDate: new Date(formatDate(bookedDate, undefined, false)),
    linkedQuotes: quote.value?.linkedQuotes,
    termConditions: handleConvertBrTag(termConditions)
  };
  adminQuoteStore
    .updateQuote(payload, sk)
    .then(() => {
      disablePreviewPDF.value = false;
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
      reloadList();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const initPayloadSubmit = () => {
  const data = cloneDeep(quote.value);
  data.airs.forEach((air) => {
    air.price.forEach((item) => {
      ['adtGbp', 'chdGbp', 'infGbp', 'adtVp', 'chdVp', 'infVp', 'aTaxes', 'marginFare', 'basePrice', 'paxFareSumAdult', 'paxFareSumChild'].forEach((key) => delete item[key]);
    });
    air.flights.forEach((flight: any) => {
      if (flight.cabin && flight.cabin.length) {
        flight.cabin = flight.cabin.join(', ');
      }
      ['arrDate', 'depDate'].forEach((key) => {
        flight[key] = formatDateTime(flight[key]);
      });
      delete flight.packages;
      delete flight.partner;
    });
  });
  return data;
};

const editExpiryDate = ref({
  isEdit: false,
  data: null
});
const editDateBooked = ref({
  isEdit: false,
  data: null
});

const editAdult = ref({
  isEdit: false,
  data: ''
});

const editAdultBusiness = ref({
  isEdit: false,
  data: ''
});

const editChild = ref({
  isEdit: false,
  data: ''
});

const editChildBusiness = ref({
  isEdit: false,
  data: ''
});

const handleSaveAdult = (adultNo, air) => {
  air.adtNo = adultNo;
  editAdult.value.isEdit = false;
};

const handleSaveAdultBusiness = (adultNo, air) => {
  air.businessAdtNo = adultNo;
  editAdultBusiness.value.isEdit = false;
};

const handleSaveChild = (childNo, air) => {
  air.chdNo = childNo;
  editChild.value.isEdit = false;
};

const handleSaveChildBusiness = (childNo, air) => {
  air.businessChdNo = childNo;
  editChildBusiness.value.isEdit = false;
};

const updateDepDateOption = (index = 0) => {
  departureDateFormat.depDate = quote.value?.airs[index]?.depDate;
};

const handleChange = (num: number) => {
  updateDepDateOption(num);
};

const addNewFlight = (airData, type) => {
  const groupFlights = airData.flights.filter((flight) => flight.type === type);
  const numberOfFlight = groupFlights?.length;
  if (numberOfFlight < 4) {
    let lastIndex = -1;
    airData.flights?.forEach((flight, index) => {
      if (flight.type === type) {
        lastIndex = index;
      }
    });
    const initFlightKey = `flight${Date.now()}${airData.flights.length}`;
    airData.flights.splice(lastIndex + 1, 0, initFlightData(type, initFlightKey));
    editingRows.value = airData.flights.filter((flight) => editingRows.value.find((row) => row.key === flight.key) || flight.key === initFlightKey);
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('worksheet.maximum_flight_notification') });
  }
};

const deleteFlight = (air, flight) => {
  air.flights = air.flights.filter((f) => f.key !== flight.key);
};

const searchGateway = (event) => {
  if (!event.query.trim().length || event.originalEvent.type !== 'input') {
    filterGateway.value = [...airportList.value.slice(0, 100)];
  } else {
    filterGateway.value = airportList.value.filter((air) => {
      return onFilterAirport(air, event);
    });
  }
};

const searchDestination = (event) => {
  if (!event.query.trim().length || event.originalEvent.type !== 'input') {
    filterDestination.value = [...airportList.value.slice(0, 100)];
  } else {
    filterDestination.value = airportList.value.filter((air) => {
      return onFilterAirport(air, event);
    });
  }
};

const handleSaveExpiry = (date) => {
  quote.value.expires = date;
  editExpiryDate.value.isEdit = false;
};

const handleSaveDateBooked = (date) => {
  quote.value.bookedDate = date;
  editDateBooked.value.isEdit = false;
};

const changeLinkedQuoteFlight = (event: any) => {
  quote.value.linkedQuotes = event;
};

const convertHeaderColspan = (air, pricing, index) => {
  let adtNoTemp = air.adtNo ?? 0;
  let chdNoTemp = air.chdNo ?? 0;
  if (pricing.type.toUpperCase() === 'BUSINESS') {
    adtNoTemp = air.businessAdtNo ?? 0;
    chdNoTemp = air.businessChdNo ?? 0;
  }
  const typeKey = `worksheet.${pricing.type.toLowerCase()}`;
  const type = `${t('common.pricing')} (${t(typeKey)})`;
  const adt = `${adtNoTemp} ${t('common.adults')}`;
  const children = `${chdNoTemp} ${t('common.children')}`;
  let result = `${type} (${adt}, ${children})`;
  if (air.price[index].suffixKey) {
    result = `${type} ${air?.price?.[index].numberOfPassengers ? formatFareBasic(air.price[index]) : ''}`;
  }
  return result;
};

const formatFareBasic = (price) => {
  const parts = price.fareBasis.split(' / ');
  return `-${parts.map((part) => part.charAt(0)).join('/')} - ${price.numberOfPassengers} ${t('client_copy.number_of_passengers').toLowerCase()}`;
};

onBeforeMount(async () => {
  const res = await Promise.all([adminQuoteStore.getAirlines()]);
  listAirline.value = res[0];
  quote.value?.airs.forEach((air) => {
    air.flights = formatFlightData(air.flights, true);
  });
  editExpiryDate.value.data = quote.value?.expires && new Date(quote.value?.expires);
  editDateBooked.value.data = quote.value?.bookedDate && new Date(quote.value?.bookedDate);
  updateDepDateOption();
});

const openPriceBreakModal = (price: any) => {
  const dialogRef = dialog.open(PriceBreakdownModal, {
    props: {
      header: t('worksheet.price_breakdown'),
      modal: true
    },
    data: {
      getPostSearch: price.breakdownPrice,
      isHiddenChild: true,
      isHiddenInfant: true
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const formatPriceLanguage = (price: number) => {
  return new Intl.NumberFormat(currentLanguage, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);
};

const handleParseFloat = (price: number) => {
  return parseFloat(price?.toFixed(2));
};

const filterFlightFollowType = (flights: any, itinerary: string) => {
  return itinerary === ONEWAY ? flights.filter((flight: any) => flight.type === 'Outbound') : flights;
};

watch(
  quote,
  (newVal) => {
    if (newVal.airs) {
      typeProcess = Array(newVal.airs.length).fill(['Economy']);
    }
    newVal.airs?.forEach((air: any, indexAir: number) => {
      expandedDetail.value[indexAir] = [];
      air.price.forEach((detail: any, index: number) => {
        expandedDetail.value[indexAir][index] = {
          Economy: typeProcess[indexAir][index] === 'Economy',
          Business: typeProcess[indexAir][index] === 'Business'
        };
      });
    });
  },
  { immediate: true, deep: true }
);
</script>
<template>
  <div class="air-worksheet">
    <section id="header" class="my-3 px-3">
      <div class="grid">
        <div class="col-6 text-lg font-bold flex justify-content-start align-items-center">{{ $t('worksheet.groups_quote') }}</div>
        <div class="col-6 text-right text-lg font-bold flex justify-content-end align-items-center">({{ $t('worksheet.flight_manual') }})</div>
      </div>
    </section>
    <section id="information" class="px-3">
      <div class="grid">
        <div class="col-3">
          <div class="quote-id font-bold">
            <div class="grid">
              <div class="col-6 mb-4">{{ $t('worksheet.quote_id') }}:</div>
              <div class="col-6 font-normal">{{ quote?.sk }}</div>
            </div>
          </div>
          <LinkedQuotesEditor :worksheetData="quote" :permission="hasUserPermission(quote, canEditHeader)" @changeLinkedQuote="changeLinkedQuoteFlight($event)"></LinkedQuotesEditor>
        </div>
        <div class="col-4">
          <div class="date-quote font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.date_quoted') }}:</div>
              <div class="col-8 font-normal">{{ formatDate(quote?.created, 'dd-LLL-yyyy', true, currentLanguage) }}</div>
            </div>
          </div>
          <div class="expiry-date font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.expiry_date') }}:</div>
              <div class="col-8 font-normal">
                <div v-if="!editExpiryDate.isEdit" class="flex align-items-top">
                  <div class="min-w-5rem">
                    {{ formatDate(quote?.expires, 'dd-LLL-yyyy', false, currentLanguage) }}
                  </div>
                  <span class="text-center ml-4">
                    <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editExpiryDate.isEdit = true"></i>
                  </span>
                </div>
                <div v-else class="flex align-items-top">
                  <div class="w-9rem">
                    <DatePicker id="calendar-24h" v-model="editExpiryDate.data" dateFormat="dd-M-yy">
                      <template #footer>
                        <p class="text-center">
                          <small>{{ $t('common.press_space') }}</small>
                        </p>
                      </template>
                    </DatePicker>
                  </div>
                  <div class="text-center ml-4 flex gap-3 align-items-center">
                    <i class="pi pi-check cursor-pointer" v-if="editExpiryDate.data" @click="handleSaveExpiry(editExpiryDate.data)"></i>
                    <i class="pi pi-times cursor-pointer" @click="editExpiryDate = { isEdit: false, data: quote?.expires && new Date(quote?.expires) }"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="book-date font-bold">
            <div class="grid">
              <div class="col-4">{{ $t('worksheet.book_dated') }}:</div>
              <div class="col-8 font-normal">
                <div v-if="!editDateBooked.isEdit" class="flex align-items-top">
                  <div class="min-w-5rem">
                    {{ formatDate(quote?.bookedDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                  </div>
                  <span class="text-center ml-4">
                    <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editDateBooked.isEdit = true"></i>
                  </span>
                </div>
                <div v-else class="flex align-items-top">
                  <div class="w-9rem">
                    <DatePicker id="calendar-24h" v-model="editDateBooked.data" dateFormat="dd-M-yy">
                      <template #footer>
                        <p class="text-center">
                          <small>{{ $t('common.press_space') }}</small>
                        </p>
                      </template>
                    </DatePicker>
                  </div>
                  <div class="text-center flex ml-4 gap-3 align-items-center">
                    <i class="pi pi-check cursor-pointer" @click="handleSaveDateBooked(editDateBooked.data)"></i>
                    <i class="pi pi-times cursor-pointer" @click="editDateBooked = { isEdit: false, data: quote?.bookedDate && new Date(quote?.bookedDate) }"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-5">
          <div class="quote-assigned-to font-bold">
            <div class="grid">
              <div class="col-4 mb-4">{{ $t('worksheet.quote_assigned_to') }}:</div>
              <div class="col-8 font-normal">{{ quote?.assignedTo === UNASSIGNED ? $t('status.unassigned') : quote?.assignedTo }}</div>
            </div>
          </div>

          <div class="book-by font-bold">
            <div class="grid">
              <div class="col-4">{{ $t('worksheet.book_by') }}:</div>
              <div class="col-8 font-normal flex gap-1">
                <div>{{ quote?.user }}</div>
                <div>{{ quote?.email && `(${quote?.email})` }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section id="details">
      <Tabs :value="0" @update:value="handleChange($event)">
        <TabList>
          <Tab v-for="(_, index) in quote?.airs" :key="index" :value="index">{{ `OPTION ${index + 1}` }}</Tab>
          <Tab v-if="hasUserPermission(quote, canViewTermConditions)" :key="quote?.airs?.length" :value="quote?.airs?.length">{{ $t('menu.term_and_condition') }}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel v-for="(air, airIndex) in quote?.airs" :key="airIndex" :value="airIndex">
            <div class="option">
              <div class="lead-option mb-2">{{ `${$t('common.flight')}` }}</div>
              <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap pr-4">
                <div>
                  <div class="itinerary font-bold mb-2 mt-2">
                    <div class="flex gap-3">
                      <div class="min-w-label">{{ $t('advance_quote.form.itinerary') }}:</div>
                      <div class="font-normal">{{ $t(`advance_quote.form.${air.itinerary.toLowerCase()}`) }}</div>
                    </div>
                  </div>
                  <div class="gateway font-bold mb-2 mt-2">
                    <div class="flex gap-3">
                      <div class="min-w-label">{{ $t('common.gateway') }}:</div>
                      <div class="font-normal">{{ `${getTextVariant(currentLanguage, air?.gate, air?.gateFr)} (${air?.gateCode})` }}</div>
                    </div>
                  </div>
                  <div class="destination font-bold mb-2">
                    <div class="flex gap-3">
                      <div class="min-w-label">{{ $t('common.destination') }}:</div>
                      <div class="font-normal">{{ `${getTextVariant(currentLanguage, air?.dest, air?.destFr)} (${air?.destCode})` }}</div>
                    </div>
                  </div>
                  <div class="returnFrom font-bold mb-2" v-if="air.itinerary === OPENJAW">
                    <div class="flex gap-3">
                      <div class="min-w-label">{{ $t('advance_quote.form.returning_from') }}:</div>
                      <div class="font-normal">{{ getTextVariant(currentLanguage, air.returnFrom, air.returnFromFr) }} ({{ air.returnFromCode }})</div>
                    </div>
                  </div>
                  <div class="returnTo font-bold mb-2" v-if="air.itinerary === OPENJAW">
                    <div class="flex gap-3">
                      <div class="min-w-label">{{ $t('advance_quote.form.returning_to') }}:</div>
                      <div class="font-normal">{{ getTextVariant(currentLanguage, air.returnTo, air.returnToFr) }} ({{ air.returnToCode }})</div>
                    </div>
                  </div>
                  <div class="hotel font-bold mb-2">
                    <div class="flex gap-3">
                      <div class="min-w-label">{{ $t('worksheet.dep_date') }}:</div>
                      <div class="font-normal">{{ formatDate(air?.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
                    </div>
                  </div>
                  <div class="hotel font-bold mb-2" v-if="air.itinerary !== ONEWAY">
                    <div class="flex gap-3">
                      <div class="min-w-label">{{ $t('worksheet.return_date') }}:</div>
                      <div class="font-normal">{{ formatDate(air?.retDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
                    </div>
                  </div>
                </div>
                <div class="flex flex-column align-items-end">
                  <div class="hotel-room-cat font-bold mb-2 mt-2">
                    <div class="flex">
                      <div class="min-w-label">{{ $t('common.class') }}:</div>
                      <div class="font-normal w-6rem">{{ getClassAir(air?.price) }}</div>
                    </div>
                  </div>
                  <div class="adults font-bold mb-2">
                    <div class="flex" v-if="!editAdult.isEdit">
                      <span class="text-center mr-4">
                        <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editAdult = { isEdit: true, data: air.adtNo }"></i>
                      </span>
                      <div class="min-w-label">{{ $t('common.adults') }}:</div>
                      <div class="font-normal w-6rem">{{ air.adtNo }}</div>
                    </div>
                    <div class="flex" v-else>
                      <div class="min-w-label">{{ $t('common.adults') }}:</div>
                      <div class="w-3rem">
                        <InputNumber v-model="editAdult.data" :min="0" />
                      </div>
                      <div class="text-center ml-2 flex gap-2 align-items-center">
                        <i class="pi pi-check cursor-pointer" @click="handleSaveAdult(editAdult.data, air)"></i>
                        <i class="pi pi-times cursor-pointer" @click="editAdult = { isEdit: false, data: air.adtNo }"></i>
                      </div>
                    </div>
                  </div>
                  <div class="child font-bold mb-2">
                    <div class="flex" v-if="!editChild.isEdit">
                      <span class="text-center mr-4">
                        <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editChild = { isEdit: true, data: air.chdNo }"></i>
                      </span>
                      <div class="min-w-label">{{ $t('common.children') }}:</div>
                      <div class="font-normal w-6rem">{{ air.chdNo }}</div>
                    </div>
                    <div class="flex" v-else>
                      <div class="min-w-label">{{ $t('common.children') }}:</div>
                      <div class="w-3rem">
                        <InputNumber v-model="editChild.data" :min="0" />
                      </div>
                      <div class="text-center ml-2 flex gap-2 align-items-center">
                        <i class="pi pi-check cursor-pointer" @click="handleSaveChild(editChild.data, air)"></i>
                        <i class="pi pi-times cursor-pointer" @click="editChild = { isEdit: false, data: air.chdNo }"></i>
                      </div>
                    </div>
                  </div>
                  <div class="total font-bold mb-2 mt-2">
                    <div class="flex">
                      <div class="min-w-label">{{ $t('worksheet.total_passengers_economy') }}:</div>
                      <div class="font-normal w-6rem">{{ air?.adtNo + air?.chdNo }}</div>
                    </div>
                  </div>
                </div>
                <div class="flex flex-column align-items-end">
                  <div class="hotel-room-cat font-bold mb-2 mt-2">
                    <div class="flex">
                      <div class="min-w-label">{{ $t('common.class') }}:</div>
                      <div class="font-normal w-6rem">{{ getClassAirBusiness(air?.price) }}</div>
                    </div>
                  </div>
                  <div class="adults font-bold mb-2">
                    <div class="flex" v-if="!editAdultBusiness.isEdit">
                      <span class="text-center mr-4">
                        <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editAdultBusiness = { isEdit: true, data: air.businessAdtNo }"></i>
                      </span>
                      <div class="min-w-label">{{ $t('common.adults') }}:</div>
                      <div class="font-normal w-6rem">{{ air.businessAdtNo ?? 0 }}</div>
                    </div>
                    <div class="flex" v-else>
                      <div class="min-w-label">{{ $t('common.adults') }}:</div>
                      <div class="w-3rem">
                        <InputNumber v-model="editAdultBusiness.data" :min="0" />
                      </div>
                      <div class="text-center ml-2 flex gap-2 align-items-center">
                        <i class="pi pi-check cursor-pointer" @click="handleSaveAdultBusiness(editAdultBusiness.data, air)"></i>
                        <i class="pi pi-times cursor-pointer" @click="editAdultBusiness = { isEdit: false, data: air.businessAdtNo }"></i>
                      </div>
                    </div>
                  </div>
                  <div class="child font-bold mb-2">
                    <div class="flex" v-if="!editChildBusiness.isEdit">
                      <span class="text-center mr-4">
                        <i v-if="hasUserPermission(quote, canEditHeader)" class="pi pi-pencil cursor-pointer" @click="editChildBusiness = { isEdit: true, data: air.businessChdNo }"></i>
                      </span>
                      <div class="min-w-label">{{ $t('common.children') }}:</div>
                      <div class="font-normal w-6rem">{{ air.businessChdNo ?? 0 }}</div>
                    </div>
                    <div class="flex" v-else>
                      <div class="min-w-label">{{ $t('common.children') }}:</div>
                      <div class="w-3rem">
                        <InputNumber v-model="editChildBusiness.data" :min="0" />
                      </div>
                      <div class="text-center ml-2 flex gap-2 align-items-center">
                        <i class="pi pi-check cursor-pointer" @click="handleSaveChildBusiness(editChildBusiness.data, air)"></i>
                        <i class="pi pi-times cursor-pointer" @click="editChildBusiness = { isEdit: false, data: air.businessChdNo }"></i>
                      </div>
                    </div>
                  </div>
                  <div class="total font-bold mb-2 mt-2">
                    <div class="flex">
                      <div class="min-w-label">{{ $t('worksheet.total_passengers_business') }}:</div>
                      <div class="font-normal w-6rem">{{ (air?.businessAdtNo ?? 0) + (air?.businessChdNo ?? 0) }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="grid mb-2 mt-2">
                <div class="min-w-label font-bold col-2">{{ $t('advance_quote.form.additional_note') }}:</div>
                <div class="font-normal col-10">
                  <InputText :value="air.additionalNote" class="w-full" disabled />
                </div>
              </div>
            </div>
            <div v-if="hasUserPermission(quote, authorizations?.worksheet_air_only?.flight_view)">
              <div class="flight-detail">
                <div class="lead-flight flex justify-content-between align-items-center">
                  <span> {{ $t('worksheet.flight_details') }} </span>
                </div>
                <div class="detail" v-if="hasUserPermission(quote, authorizations?.worksheet_air_only?.flight_view)">
                  <DataTable
                    v-model:editingRows="editingRows"
                    :value="filterFlightFollowType(air?.flights, air.itinerary)"
                    rowGroupMode="subheader"
                    groupRowsBy="groupBy"
                    class="w-full"
                    showGridlines
                    editMode="row"
                    @row-edit-save="onRowEditSave($event, air?.flights)"
                  >
                    <template #groupheader="slotProps">
                      <div class="flex align-items-center justify-content-end gap-2">
                        <div class="add-flight-button" @click="addNewFlight(air, slotProps.data.type)">
                          <span v-if="air.itinerary === ONEWAY">+ {{ $t('management.airline.add_flight') }}</span>
                          <span v-else>+ {{ $t(`worksheet.add_${slotProps.data.type?.toLowerCase()}`) }}</span>
                        </div>
                      </div>
                    </template>
                    <TableColumn field="groupBy"></TableColumn>
                    <TableColumn field="type" :header="$t('worksheet.type')" dataType="string" class="min-w-5rem">
                      <template #body="{ data }">
                        {{ $t(`worksheet.${data.type?.toLowerCase()}`) }}
                      </template>
                    </TableColumn>
                    <TableColumn field="operated" :header="$t('worksheet.airline')" dataType="string" class="min-w-9rem">
                      <template #body="{ data }">
                        {{ data.airline }}
                      </template>
                      <template #editor="{ data, field }">
                        <Select v-model="data[field]" :options="listAirline" optionLabel="name" optionValue="code" :placeholder="$t('worksheet.select_type')" class="w-full" />
                      </template>
                    </TableColumn>
                    <TableColumn field="flightNumber" :header="$t('worksheet.flight_number')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        {{ data.flightNumber }}
                      </template>
                      <template #editor="{ data, field }">
                        <InputText v-model="data[field]" :maxlength="5" @input="validateFlightNumber(data, field)" />
                      </template>
                    </TableColumn>
                    <TableColumn field="gate" :header="$t('worksheet.from')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        {{ getTextVariant(currentLanguage, data.gate, data.gateFr) }}
                      </template>
                      <template #editor="{ data, field }">
                        <AutoComplete v-model="data[field]" :suggestions="filterGateway" @complete="searchGateway($event)" completeOnFocus optionLabel="selectedLabel" class="w-full" forceSelection>
                          <template #option="slotProps">
                            <div class="flex align-options-center">
                              <div>
                                <div>{{ slotProps.option.airportName }} ({{ slotProps.option.airportCode }})</div>
                                <div class="text-sm">{{ slotProps.option.cityName }}, {{ slotProps.option.province || slotProps.option.region }}, {{ slotProps.option.countryName }}</div>
                              </div>
                            </div>
                          </template>
                        </AutoComplete>
                      </template>
                    </TableColumn>
                    <TableColumn field="dest" :header="$t('worksheet.to')" dataType="string" class="min-w-10rem">
                      <template #body="{ data }">
                        {{ getTextVariant(currentLanguage, data.dest, data.destFr) }}
                      </template>
                      <template #editor="{ data, field }">
                        <AutoComplete v-model="data[field]" :suggestions="filterDestination" @complete="searchDestination($event)" completeOnFocus optionLabel="selectedLabel" class="w-full" forceSelection>
                          <template #option="slotProps">
                            <div class="flex align-options-center">
                              <div>
                                <div>{{ slotProps.option.airportName }} ({{ slotProps.option.airportCode }})</div>
                                <div class="text-sm">{{ slotProps.option.cityName }}, {{ slotProps.option.province || slotProps.option.region }}, {{ slotProps.option.countryName }}</div>
                              </div>
                            </div>
                          </template>
                        </AutoComplete>
                      </template>
                    </TableColumn>
                    <TableColumn field="depDate" :header="$t('worksheet.dep_date')" class="min-w-10rem">
                      <template #body="{ data }">
                        {{ formatDate(data?.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                      </template>
                      <template #editor="{ data, field }">
                        <DatePicker id="calendar-24h" v-model="data[field]" dateFormat="dd-M-yy">
                          <template #footer>
                            <p class="text-center">
                              <small>{{ $t('common.press_space') }}</small>
                            </p>
                          </template>
                        </DatePicker>
                      </template>
                    </TableColumn>
                    <TableColumn field="depTime" :header="$t('worksheet.dep_time')" dataType="date" class="min-w-8rem">
                      <template #body="{ data }">
                        {{ data.depTime }}
                      </template>
                      <template #editor="{ data, field }">
                        <DatePicker id="calendar-24h" v-model="data[field]" showTime hourFormat="24" timeOnly>
                          <template #footer>
                            <p class="text-center">
                              <small>{{ $t('common.press_space') }}</small>
                            </p>
                          </template>
                        </DatePicker>
                      </template>
                    </TableColumn>
                    <TableColumn field="arrDate" :header="$t('worksheet.arrival_date')" class="min-w-10rem">
                      <template #body="{ data }">
                        {{ formatDate(data?.arrDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                      </template>
                      <template #editor="{ data, field }">
                        <DatePicker id="calendar-24h" v-model="data[field]" dateFormat="dd-M-yy">
                          <template #footer>
                            <p class="text-center">
                              <small>{{ $t('common.press_space') }}</small>
                            </p>
                          </template>
                        </DatePicker>
                      </template>
                    </TableColumn>
                    <TableColumn field="arrTime" :header="$t('worksheet.arrival_time')" dataType="date" class="min-w-8rem">
                      <template #body="{ data }">
                        {{ data.arrTime }}
                      </template>
                      <template #editor="{ data, field }">
                        <DatePicker id="calendar-24h" v-model="data[field]" showTime hourFormat="24" timeOnly>
                          <template #footer>
                            <p class="text-center">
                              <small>{{ $t('common.press_space') }}</small>
                            </p>
                          </template>
                        </DatePicker>
                      </template>
                    </TableColumn>
                    <TableColumn field="via" :header="$t('worksheet.via')" dataType="date" class="w-5rem min-w-5rem">
                      <template #body="{ data }">
                        {{ data.via }}
                      </template>
                      <template #editor="{ data, field }">
                        <InputText v-model="data[field]" :maxlength="3" />
                      </template>
                    </TableColumn>
                    <TableColumn field="fClass" :header="$t('common.class')" dataType="date" class="w-5rem">
                      <template #body="{ data }">
                        <span>{{ data.fClass }}</span>
                      </template>
                      <template #editor="{ data, field }">
                        <InputText v-model="data[field]" :maxlength="5" />
                      </template>
                    </TableColumn>
                    <TableColumn field="cabin" :header="$t('common.cabin')" dataType="string" class="word-break w-10rem max-w-10rem">
                      <template #body="{ data, field }">
                        {{ getCabinType(data[field]) }}
                      </template>
                      <template #editor="{ data, field }">
                        <MultiSelect v-model="data[field]" :options="LIST_CABIN_TYPE" :maxSelectedLabels="1" optionLabel="name" optionValue="data" class="w-10rem" />
                      </template>
                    </TableColumn>
                    <TableColumn v-if="hasUserPermission(quote, authorizations?.worksheet_air_only?.flight_edit)" :rowEditor="true" class="text-center relative">
                      <template #body="{ editorInitCallback, data }">
                        <div class="flex flex-column">
                          <i class="pi pi-pencil table-action-button" @click="editorInitCallback()"></i>
                          <i v-if="data.canDelete" class="pi pi-times-circle table-action-button text-primary" @click="deleteFlight(air, data)"></i>
                        </div>
                      </template>
                      <template #editor="{ editorSaveCallback, editorCancelCallback }">
                        <div class="flex flex-column">
                          <i class="pi pi-check table-action-button" @click="editorSaveCallback()"></i>
                          <i class="pi pi-times table-action-button" @click="editorCancelCallback()"></i>
                        </div>
                      </template>
                    </TableColumn>
                  </DataTable>
                </div>
              </div>
              <div class="pricing my-3" v-for="(pricing, index) in formatPrice(air?.price, authorizations?.worksheet_air_only)" :key="index">
                <div class="lead-pricing flex justify-content-between cursor-pointer" @click="expandPricingFunc(pricing.type, airIndex, index)">
                  <div class="flex align-items-center gap-10">
                    <div>{{ convertHeaderColspan(air, pricing, index) }}</div>
                    <div class="flex align-items-center ml-2">[&nbsp; <i class="pi pi-minus" v-if="expandedDetail[airIndex][index][pricing.type]"></i> <i class="pi pi-plus" v-else></i>&nbsp;]</div>
                  </div>
                  <Button
                    v-if="isProcessButton(air, pricing.type, index) && hasUserPermission(quote, authorizations?.worksheet_air_only?.search_flight_edit)"
                    size="small"
                    @click.stop="openFlightModal(air, airIndex, pricing, index, quote)"
                    severity="danger"
                    class="custom-buttom action-button"
                    :label="$t('common.buttons.process')"
                  ></Button>
                </div>
                <div v-show="expandedDetail[airIndex][index][pricing.type]">
                  <div class="detail-pricing">
                    <DataTable v-model:editingRows="editingPriceRows" :value="pricing.rows" class="w-full" showGridlines editMode="row" @row-edit-save="changePricingFlight($event, air.price[index], pricing.rows)">
                      <TableColumn field="rowName" dataType="string" class="min-w-10rem h-3rem font-bold relative">
                        <template #body="{ data, field }">
                          <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                            {{ data[field] && $t(data[field]) }} <i v-if="data.rowName === 'worksheet.agency_taxes'" class="pi pi-plus-circle ml-1 mt-1 cursor-pointer" @click="openPriceBreakModal(air.price[index])"></i>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="fare" :header="$t('worksheet.fare_type')" dataType="number" class="min-w-10rem h-3rem relative">
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index !== 0 && index !== 1" :minFractionDigits="2" v-model="data[field]" />
                          <div v-else :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                            <div>{{ data[field] ? formatPriceLanguage(Number(data[field])) : '0.00' }}</div>
                          </div>
                        </template>
                        <template #body="{ data, field }">
                          <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                            <div>{{ data[field] ? formatPriceLanguage(Number(data[field])) : '0.00' }}</div>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="adt" :header="$t('worksheet.12_and_up')" dataType="number" class="min-w-10rem h-3rem relative">
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index === 0 || index === 1" :minFractionDigits="2" v-model="data[field]" />
                        </template>
                        <template #body="{ data, field }">
                          <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                            <div>{{ data[field] ? formatPriceLanguage(Number(data[field])) : '' }}</div>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="chd" :header="$t('common.child_2_11')" dataType="number" class="min-w-10rem h-3rem relative">
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index === 0 || index === 1" :minFractionDigits="2" v-model="data[field]" />
                        </template>
                        <template #body="{ data, field }">
                          <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                            <div>{{ data[field] ? formatPriceLanguage(Number(data[field])) : '' }}</div>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="inf" :header="$t('common.infant_0_1')" dataType="number" class="min-w-10rem h-3rem relative">
                        <template #editor="{ data, field, index }">
                          <InputNumber v-if="index === 0 || index === 1" :minFractionDigits="2" v-model="data[field]" />
                        </template>
                        <template #body="{ data, field }">
                          <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                            <div>{{ data[field] ? formatPriceLanguage(Number(data[field])) : '' }}</div>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="detail" :header="$t('worksheet.baggage_details')" dataType="number" class="break-word min-w-10rem h-3rem relative">
                        <template #editor="{ data, field, index }">
                          <InputText v-if="index === 0" v-model="data[field]" />
                        </template>
                        <template #body="{ data, field, index }">
                          <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                            <p v-if="index == 0">{{ data[field] }}</p>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn v-if="hasUserPermission(quote, false)" :header="$t('common.action')" class="edit-button w-8rem text-center h-3rem relative" :rowEditor="true">
                        <template #body="{ editorInitCallback, data }">
                          <div :class="[data.key === 'total' ? 'font-bold p-3 text-900 absolute top-0 right-0 left-0 bottom-0 background-opacity' : 'font-medium']">
                            <i v-if="data.key !== 'total' && data.canEdit" class="pi pi-pencil" @click="editorInitCallback"></i>
                          </div>
                          <p v-if="data.key === 'total'" class="opacity-0"></p>
                        </template>
                        <template #editor="{ editorSaveCallback, editorCancelCallback }">
                          <div class="flex">
                            <i class="pi pi-check edit-pi" @click="editorSaveCallback"></i>
                            <i class="pi pi-times edit-pi" @click="editorCancelCallback"></i>
                          </div>
                        </template>
                      </TableColumn>
                    </DataTable>
                  </div>
                </div>
              </div>
              <div class="promotions" v-if="hasUserPermission(quote, canViewPromotion)">
                <div class="lead-promotions">{{ $t('worksheet.promotions') }}</div>
                <div v-if="promoeditedData.index !== `airIndex${airIndex}`" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="air.promotion" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(quote, canEditPromotion)" class="pi pi-pencil edit-pi cursor-pointer" @click="promoeditedData = { index: `airIndex${airIndex}`, data: air.promotion }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="promoeditedData.data" class="custom-editor" />
                  </div>
                  <div class="flex flex-col text-center">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSavePromo(air)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="promoeditedData.index = null"></i>
                  </div>
                </div>
              </div>
              <div class="agency-comment pt-3" v-if="hasUserPermission(quote, canViewAgencyComment)">
                <div class="lead-comment">{{ $t('worksheet.agency_comment') }}</div>
                <div v-if="editComment.index !== `airIndex${airIndex}`" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="air.comment" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(quote, canEditAgencyComment)" class="pi pi-pencil edit-pi cursor-pointer" @click="editComment = { index: `airIndex${airIndex}`, data: air.comment }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editComment.data" class="custom-editor" />
                  </div>
                  <div class="flex flex-col text-center">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="saveComment(air, `airIndex${airIndex}`)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editComment.index = null"></i>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel :value="quote?.airs?.length">
            <div class="term_of_c">
              <div class="intro">
                <h4 class="font-bold text-lg">{{ $t('common.intro') }}</h4>
                <div v-if="!editIntro.isEdit" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="quote.intro" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(quote, canEditTermConditions)" class="pi pi-pencil edit-pi cursor-pointer" @click="editIntro = { isEdit: true, data: quote.intro }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editIntro.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveIntro(quote)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editIntro.isEdit = false"></i>
                  </div>
                </div>
              </div>
              <div class="term_conditions mt-4">
                <h4 class="font-bold text-lg">{{ $t('menu.term_and_condition') }}</h4>
                <div v-if="!editTermConditions.isEdit" class="flex">
                  <div class="w-full">
                    <HTMLEditor :readonly="true" v-model="quote.termConditions" class="custom-editor" />
                  </div>
                  <span class="text-center">
                    <i v-if="hasUserPermission(quote, canEditTermConditions)" class="pi pi-pencil edit-pi cursor-pointer" @click="editTermConditions = { isEdit: true, data: quote.termConditions }"></i>
                  </span>
                </div>
                <div v-else class="flex">
                  <div class="w-full">
                    <HTMLEditor v-model="editTermConditions.data" class="custom-editor" />
                  </div>
                  <div class="text-center flex flex-col">
                    <i class="pi pi-check edit-pi cursor-pointer" @click="handleSaveTermCondition(quote)"></i>
                    <i class="pi pi-times edit-pi cursor-pointer" @click="editTermConditions.isEdit = false"></i>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
      <div class="flex justify-content-center mt-2 gap-3" v-if="hasUserPermission(quote, false)">
        <Button id="flight-save-btn" :label="$t('promos.save')" icon="pi pi-check" class="p-button action-button" @click="handleUpdateQuote" />
        <ViewPDFFile :quoteID="quote.sk ?? ''" :disabled="disablePreviewPDF" isShowBtn></ViewPDFFile>
      </div>
    </section>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .p-inputtextarea:disabled {
    opacity: 1;
    cursor: not-allowed;
  }

  .background-opacity {
    background: rgba($color: #ddd, $alpha: 0.5);
  }

  .flight-detail {
    table {
      td,
      th {
        padding: 1rem 0.75rem !important;
      }
    }
  }
}

:deep(.p-inputnumber .p-inputnumber-input) {
  width: 100%;
}
</style>
